import React from "react";
import { Link } from "react-router-dom";
import {
  IoPlayCircleOutline,
  IoCheckmarkCircleOutline,
  IoCubeOutline,
  IoLockClosedOutline,
  IoOptionsOutline,
  IoPhonePortraitOutline,
  IoPulseOutline,
  IoTimeOutline,
  IoTrendingUpOutline,
} from "react-icons/io5";
import { asIcon } from "../../components/Landing/ProjectSection";
import { RESEARCH } from "../../data/research";
import { useReveal } from "../../hooks/useReveal";
import styles from "./HowItWorks.module.css";

/** Three steps of a single home session. */
const SESSION_STEPS: readonly { title: string; description: string }[] = [
  {
    title: "Phone-based feedback",
    description:
      "Your phone's sensors capture motion during practice so you can review trends in the app.",
  },
  {
    title: "Printed accessories",
    description:
      "Lightweight accessories are produced on demand where offered. Timing depends on your order.",
  },
  {
    title: "Self-paced practice",
    description:
      "Choose sessions that fit your schedule. Progress is for personal motivation, not a clinical outcome claim.",
  },
];

/** What a session asks of you. */
const NEEDS: readonly { icon: React.ElementType; label: string; note: string }[] = [
  {
    icon: asIcon(IoPhonePortraitOutline),
    label: "Your phone",
    note: "The sensors you already carry",
  },
  {
    icon: asIcon(IoCubeOutline),
    label: "Optional printed tools",
    note: "A mug and bowl you can hold",
  },
  {
    icon: asIcon(IoTimeOutline),
    label: "About 20 minutes",
    note: "Whenever your routine allows",
  },
];

/** The gap this page is answering and the answer. */
const PAIN_POINTS: readonly string[] = [
  "Home movement habits are hard to sustain without simple feedback loops.",
  "Between visits, it can be unclear how often patients practiced.",
  "Teams want lightweight ways to encourage consistency, not new clinical obligations.",
];

const SOLUTION_POINTS: readonly string[] = [
  "No extra hardware in the clinic to manage",
  "Structured home tasks patients can try on their own schedule",
  "Dashboard views for engagement, not clinical decision support",
];

const BENEFITS: readonly { title: string; text: string; icon: React.ElementType }[] =
  [
    {
      title: "Engagement signals",
      text: "Session completion and simple movement metrics in one place, for motivation, not diagnosis.",
      icon: asIcon(IoPulseOutline),
    },
    {
      title: "Demo videos",
      text: "Patients can watch a demo of each activity to see how to perform it.",
      icon: asIcon(IoPlayCircleOutline),
    },
    {
      title: "Trends over time",
      text: "Charts for discussion and education, not substitutes for clinical judgment.",
      icon: asIcon(IoTrendingUpOutline),
    },
    {
      title: "Privacy-aware design",
      text: "Account-based access; align use with your organization's privacy program.",
      icon: asIcon(IoLockClosedOutline),
    },
    {
      title: "No external hardware",
      text: "Uses the patient's phone alongside optional printed accessories.",
      icon: asIcon(IoPhonePortraitOutline),
    },
    {
      title: "Flexible workflows",
      text: "You choose how (or whether) to incorporate home practice into your programs.",
      icon: asIcon(IoOptionsOutline),
    },
  ];

const CheckIcon = asIcon(IoCheckmarkCircleOutline);

function Hero() {
  return (
    <section
      className="ui-hero-panel ui-hero-panel--split"
      aria-labelledby="hiw-hero-title"
    >
      <div className="ui-hero-copy">
        <span className="ui-section-label">How it works</span>
        <h1 id="hiw-hero-title">How mRehab fits into your day</h1>
        <p>
          Short, guided upper-limb practice at home using your phone and optional
          printed accessories, built to support stroke recovery alongside your
          clinician.
        </p>
        <Link to="/shopping" className="ui-btn ui-btn--inverted">
          View the starter kit
        </Link>
      </div>
      <div className="ui-hero-media">
        <img
          src="/images/product/mug-and-bowl-still-image.jpeg"
          alt="Pouring water between printed practice tools during a home session"
          loading="eager"
          fetchPriority="high"
        />
      </div>
    </section>
  );
}

function NeedsStrip() {
  return (
    <ul className={styles.needs} aria-label="What a session needs">
      {NEEDS.map((need) => {
        const Icon = need.icon;
        return (
          <li key={need.label} className={styles.need}>
            <span className="ui-icon-chip" aria-hidden>
              <Icon />
            </span>
            <span className={styles.needText}>
              <span className={styles.needLabel}>{need.label}</span>
              <span className={styles.needNote}>{need.note}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Home practice */
function SessionSection() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`ui-reveal ${styles.session}`}
      aria-labelledby="hiw-home-title"
      ref={ref}
    >
      <figure className={styles.sessionMedia}>
        <img
          src="/images/product/vertical-mug-activity-new-2.png"
          alt="Practicing a vertical mug lift with the mRehab app"
          loading="lazy"
          decoding="async"
        />
      </figure>

      <div className={styles.sessionBody}>
        <div className={styles.head}>
          <h2 id="hiw-home-title" className={styles.title}>
            Built for home practice
          </h2>
          <p className={styles.lead}>
            Use mRehab as part of your rehab routine: repeatable tasks, feedback
            from your phone, and optional printed tools, coordinated with your care
            team when they are involved.
          </p>
        </div>

        <h3 className={styles.subhead}>What a session looks like</h3>
        <ol className={styles.timeline}>
          {SESSION_STEPS.map((step, i) => (
            <li key={step.title} className={styles.tlItem}>
              <span className={styles.tlNum} aria-hidden>
                {i + 1}
              </span>
              <h4 className={styles.tlTitle}>{step.title}</h4>
              <p className={styles.tlBody}>{step.description}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** The kit */
function KitBand() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`${styles.band} ui-reveal`}
      aria-labelledby="hiw-kit-title"
      ref={ref}
    >
      <div className={`ui-band-inner ${styles.bandGrid}`}>
        <div className={styles.kitCopy}>
          <span className={`ui-section-label ${styles.bandLabel}`}>The kit</span>
          <h2 id="hiw-kit-title" className={styles.title}>
            Simple building blocks
          </h2>
          <p className={styles.lead}>
            Optional tools you can hold and manipulate, plus in-app guidance, help
            you repeat movement tasks and notice patterns over time. Use them
            alongside advice from your own healthcare professionals, not instead of
            it.
          </p>
          <p className={styles.kitNote}>
            Printed practice tools and app feedback for home sessions between clinic
            visits.
          </p>
          <Link to="/shopping" className="ui-btn">
            View the starter kit
          </Link>
        </div>

        <div className={styles.kitFrames}>
          <figure className={`${styles.kitFrame} ${styles.kitFrameWide}`}>
            <img
              src="/images/product/mug-and-bowl-still-image-old.jpeg"
              alt="Printed mug and bowl practice tools from the mRehab kit"
              loading="lazy"
              decoding="async"
            />
          </figure>
          <figure className={`${styles.kitFrame} ${styles.kitFrameTall}`}>
            <img
              src="/images/product/vertial-mug-varun-clear-shot-new.png"
              alt="The printed mug held upright with a phone seated inside it"
              loading="lazy"
              decoding="async"
            />
          </figure>
        </div>
      </div>
    </section>
  );
}

/** The gap and the answer */
function ContrastSection() {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div className={`ui-reveal ${styles.contrast}`} ref={ref}>
      <section className={styles.contrastCol} aria-labelledby="hiw-problem-title">
        <h2 id="hiw-problem-title" className={styles.colTitle}>
          Practice fades without structure
        </h2>
        <ul className="ui-bullets">
          {PAIN_POINTS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p className={styles.colNote}>
          A wellness-oriented kit and app can offer optional structure. Your
          clinical decisions stay yours.
        </p>
      </section>

      <section className={styles.contrastCol} aria-labelledby="hiw-solution-title">
        <h2 id="hiw-solution-title" className={styles.colTitle}>
          Phone-based practice plus optional tools
        </h2>
        <p className={styles.colLead}>
          mRehab uses the smartphone patients already carry, with optional printed
          accessories, to support self-guided movement practice at home. It is for
          general wellness, not treatment delivery.
        </p>
        <ul className={styles.checks}>
          {SOLUTION_POINTS.map((point) => (
            <li key={point}>
              <CheckIcon aria-hidden className={styles.checkIcon} />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** What teams evaluate */
function EvaluateBand() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`${styles.band} ${styles.bandWash} ui-reveal`}
      aria-labelledby="hiw-benefits-title"
      ref={ref}
    >
      <div className="ui-band-inner">
        <div className={styles.head}>
          <span className={`ui-section-label ${styles.bandLabel}`}>
            For care teams
          </span>
          <h2 id="hiw-benefits-title" className={styles.title}>
            What teams evaluate
          </h2>
          <p className={styles.lead}>
            Wellness-oriented features you can pilot with patients who want
            structured home practice, without medical outcome claims from us.
          </p>
        </div>

        <ul className={styles.benefits}>
          {BENEFITS.map(({ title, text, icon: Icon }) => (
            <li key={title} className={styles.benefit}>
              <Icon aria-hidden className={styles.benefitIcon} />
              <h3 className={styles.benefitTitle}>{title}</h3>
              <p className={styles.benefitBody}>{text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Patients and provider teams */
function AudienceSection() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`ui-reveal ${styles.audience}`}
      aria-labelledby="hiw-audience-title"
      ref={ref}
    >
      <div className={styles.head}>
        <h2 id="hiw-audience-title" className={styles.title}>
          For patients and provider teams
        </h2>
        <p className={styles.lead}>
          Patients use short, self-paced sessions at home. Provider teams can review
          engagement trends in dashboards, share them with colleagues, and decide
          fit for their setting.
        </p>
      </div>

      <div className={styles.audienceGrid}>
        <figure className={`${styles.audienceShot} ${styles.audienceShotHome}`}>
          <img
            src="/images/user Demo.jpg"
            alt="A person practicing with the mRehab activity box at a kitchen table"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <figure className={`${styles.audienceShot} ${styles.audienceShotMug}`}>
          <img
            src="/images/product/vertical-mug-activity-new.png"
            alt="Lifting the printed mug with a phone seated inside during a vertical mug activity"
            loading="lazy"
            decoding="async"
          />
        </figure>
      </div>
    </section>
  );
}

function ClosingPanel() {
  return (
    <section
      className={`ui-hero-panel ui-hero-panel--subtle ${styles.closing}`}
      aria-labelledby="hiw-closing-title"
    >
      <div className="ui-hero-copy">
        <h2 id="hiw-closing-title">Ready to explore?</h2>
        <p>
          Browse the kit, read the research, or contact us with questions.
        </p>
        <div className="ui-btn-row">
          <Link to="/shopping" className="ui-btn">
            View the starter kit
          </Link>
          <Link to="/contact" className="ui-btn ui-btn--secondary">
            Contact us
          </Link>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <main id="main" className={`ui-page ${styles.page}`}>
      <div className={`ui-container ${styles.heroBlock}`}>
        <Hero />
        <NeedsStrip />
      </div>

      <div className={`ui-container ${styles.block}`}>
        <SessionSection />
      </div>

      <KitBand />

      <div className={`ui-container ${styles.block}`}>
        <ContrastSection />
      </div>

      <EvaluateBand />

      <div className={`ui-container ${styles.block}`}>
        <AudienceSection />
        <p>
          {RESEARCH.homeTeaser}{" "}
          <Link to="/research" className="ui-link research-text-link">
            Read the research
          </Link>
        </p>
        <ClosingPanel />
      </div>
    </main>
  );
}
